import { Injectable } from '@angular/core';
import { ProductAnalyticsService, ProductAnalyticsEvent } from './product-analytics.service';
import {
  ITaskSupplierScheduleStoreTask,
  TaskSupplierScheduleStore,
} from 'src/app/components/task-supplier-schedule/task-supplier-schedule-store';
import { IJobScreen } from 'src/app/interfaces/job/job';
import { OrganisationTypeEnum } from 'src/app/enums/organisation-types';
import { FormGroup } from '@angular/forms';
import { ControlsOfFlat } from 'src/app/utils/controls-of';
import { timeToHoursMinutesSeconds } from 'src/app/utils/timeToHoursMinutes';
import { meterToMilesHelper } from 'src/app/utils/meter-to-miles.helper';
import { TaskCollectionTravelMode } from '../../enums/task-collection-travel-mode.enum';
import { getTransportModeText } from '../../utils/travel-scheduler-utils';
import { ISupplierSearchInboxFilter } from '../../interfaces/supplier-search-inbox-job-filter';

interface TaskDetail {
  taskId: number;
  pickupLocation: string;
  dropoffLocation: string;
  pickupDate: string;
  dropoffDate: string;
  distance: number;
  estimatedTime: string;
  transportModeSelected: string;
}

interface JobOrder {
  orderNumber: string;
  taskId: string;
  purchaseNumber: string;
  orderStatus: string;
  customerMovementType: string;
}

interface Vehicle {
  vehicleRegistration: string;
  vehicleName: string;
  vehicleMake: string;
  vehicleKerbWeight: number;
  vehicleColor: string;
}

interface JobTask {
  taskId: number;
  pickupLocation: string;
  earliestPickupCollectionDate: string;
  latestPickupCollectionDate: string;
  dropoffLocation: string;
  earliestDropoffDeliveryDate: string;
  latestDropoffDeliveryDate: string;
  distance: number;
  estimatedTime: string;
  vehicle: Vehicle;
}

export interface AnalyticsJobComponent {
  jobNumber: string;
  journeyLeg: number;
  referrerPage: string;
  tasks: ITaskSupplierScheduleStoreTask[];
  totalEarnings: number;
}

@Injectable({ providedIn: 'root' })
export class JobAnalyticsService {
  constructor(private productAnalyticsService: ProductAnalyticsService) {}

  private getTravelModeString(travelMode: TaskCollectionTravelMode) {
    switch (travelMode) {
      case 0:
        return 'Driving';
      case 1:
        return 'Bus';
      case 2:
        return 'Walking';
      case 3:
        return 'Cycling';
      default:
        return 'none';
    }
  }

  private getJobTasks(tasks: ITaskSupplierScheduleStoreTask[]): TaskDetail[] {
    return tasks.map((res) => {
      return {
        taskId: res.id,
      } as TaskDetail;
    });
  }

  sendGrabJobEvent(job: AnalyticsJobComponent) {
    const taskDetails = this.getJobTasks(job.tasks);
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.GrabJob, {
      jobId: job.jobNumber,
      journeyLeg: job.journeyLeg,
      referrerPage: job.referrerPage,
      jobTasks: taskDetails,
      totalEarnings: job.totalEarnings,
    });
  }

  sendGrabNavigationEvent(grabName: string) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.GrabNavigationEvent, {
      grabName,
    });
  }

  sendGrabJobExitEvent(job: AnalyticsJobComponent) {
    const taskDetails = this.getJobTasks(job.tasks);
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.GrabJobExit, {
      jobNumber: job.jobNumber,
      exitType: 'close_icon', // cancel_button
      journeyLeg: job.journeyLeg,
      referrerPage: job.referrerPage,
      jobTasks: taskDetails,
      totalEarnings: job.totalEarnings,
    });
  }

  sendGrabJobContinueEvent(job: AnalyticsJobComponent) {
    const taskDetails = this.getJobTasks(job.tasks);
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.GrabJobModalContinue, {
      jobId: job.jobNumber,
      journeyLeg: job.journeyLeg,
      referrerPage: job.referrerPage,
      jobTasks: taskDetails,
      totalEarnings: job.totalEarnings,
    });
  }

  sendGrabJobSummaryEvent(job: AnalyticsJobComponent) {
    const taskDetails = this.getJobTasks(job.tasks);
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.GrabJobModalSummary, {
      jobNumber: job.jobNumber,
      journeyLeg: job.journeyLeg,
      referrerPage: job.referrerPage,
      jobTasks: taskDetails,
      totalEarnings: job.totalEarnings,
    });
  }

  private getJobOrders(job: IJobScreen): JobOrder[] {
    return job.orders.map((res) => {
      return {
        customerMovementType: res?.taskTypeId?.toString(),
        orderNumber: res?.orderNumber,
        orderStatus: res?.statusId?.toString(),
        purchaseNumber: res?.purchaseOrderNo,
        taskId: res.tasks.map((t) => t.id).join(','),
      } as JobOrder;
    });
  }

  private getJobScreenTasks(job: IJobScreen): JobTask[] {
    const jobTask = [];
    job.orders.forEach((o) => {
      o.tasks.forEach((t) => {
        const estimateTimeInMinutes = Math.round(
          timeToHoursMinutesSeconds(t.estimateTime, 'minutes'),
        );
        const miles = meterToMilesHelper(t.distance);
        const task = {
          distance: miles,
          dropoffLocation: t?.dropoffLocation?.primaryAddress,
          earliestDropoffDeliveryDate: t.dropoffEarliestDateTime,
          earliestPickupCollectionDate: t.pickupEarliestDateTime,
          estimatedTime: estimateTimeInMinutes ?? '',
          latestDropoffDeliveryDate: t.dropoffLatestDateTime,
          latestPickupCollectionDate: t.pickupLatestDateTime,
          pickupLocation: t?.pickupLocation?.primaryAddress,
          taskId: t.id,
          vehicle: {
            vehicleColor: t?.vehicle?.color,
            vehicleKerbWeight: t?.vehicle?.kerbWeight,
            vehicleMake: t?.vehicle?.make,
            vehicleName: t?.vehicle?.body,
            vehicleRegistration: t?.vehicle?.registrationNumber,
          },
        } as JobTask;
        jobTask.push(task);
      });
    });
    return jobTask;
  }

  sendJobDetailsViewEvent(job: IJobScreen, organisationTypeId: number) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.JobDetailsView, {
      jobId: job.jobNumber,
      journeyLeg: job.orders.length,
      jobStatus: job.statusId,
      jobOrders: this.getJobOrders(job),
      jobTasks: this.getJobScreenTasks(job),
      userType:
        organisationTypeId === OrganisationTypeEnum.Customer
          ? 'Customer'
          : organisationTypeId === OrganisationTypeEnum.Engineius
          ? 'Engineius'
          : 'Supplier',
      isFirstJobViewedInSession: false,
      viewTimestamp: new Date().toISOString(),
    });
  }

  sendHideJobEvent(
    jobId: number,
    vehicleRegistration: string,
    hideJobReason: string,
    hideJobReasonValue: string,
  ) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.HideJobInteraction, {
      jobId: jobId,
      vehicleRegistration: vehicleRegistration,
      hideJobReason: hideJobReason,
      hideJobReasonValue: hideJobReasonValue,
    });
  }

  sendJobFilterApplyEvent(formType: string, formValue: string) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.JobFilterApply, {
      filterType: formType,
      filterValue: formValue,
    });
  }

  sendJobSearchEvent(
    filters: ISupplierSearchInboxFilter,
    pageStayDurationSeconds: number,
    isPageExit: boolean = false,
  ) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.JobSearch, {
      ...filters,
      pageStayDurationSeconds,
      isPageExit,
    });
  }

  sendWatchJobEvent(jobId: number) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.WatchJob, {
      jobId: jobId,
    });
  }

  sendTaskSwitchEvent(taskId: number, jobNumber: string) {
    void this.productAnalyticsService.trackEvent(ProductAnalyticsEvent.TaskSwitch, {
      taskId,
      jobNumber,
    });
  }
}
